import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { ServerResponse } from 'node:http';
import redirectRouteToDomain from './redirectRouteToDomain';
import redirectToRoute from './redirectToRoute';
import profileBySlugQuery from './queries/products/profileBySlugQuery.graphql';
import productExistenceQuery from './queries/products/productExistenceQuery.graphql';
import profileBySlugVariables from './profileBySlugVariables';

import { IncomingMessageWithLocale } from './getRedirectJob';

const routeNames = {
  products: 'company-profile-products',
  'fashion-finder': 'company-profile-fashion-finder',
} as const;

const getRedirectCompanyProducts = (
  mode: 'products' | 'fashion-finder',
) => {
  const redirectDomain = redirectRouteToDomain(routeNames[mode], []);

  return async (context: {
    req?: IncomingMessageWithLocale;
    res?: ServerResponse;
    apolloClient: ApolloClient<NormalizedCacheObject>;
    query: {
      slug: string;
    };
  }) => {
    const {
      req, res, apolloClient, query,
    } = context;

    redirectDomain({ req, res, query });

    if (res?.finished) {
      return {};
    }

    if (!req) {
      return {};
    }

    const { slug } = query;
    const { locale } = req;

    const { data: { profileBySlug: profile } } = await apolloClient.query({
      query: profileBySlugQuery,
      variables: profileBySlugVariables(slug),
      fetchPolicy: 'network-only',
    });

    if (!profile || profile.type !== 'COMPANY') {
      return {};
    }

    const { brands } = profile;

    if (brands.length === 0) {
      redirectToRoute('company-profile', { slug }, res, 307);
      return {};
    }

    const { data } = await apolloClient.query({
      query: productExistenceQuery,
      variables: {
        brandIds: brands.map(({ id }: { id: number }) => id),
        locales: [locale],
      },
      fetchPolicy: 'network-only',
    });

    const {
      marketProducts,
      marketProductsHidden,
      fashionFinderProducts,
    } = data;
    const premium = profile?.page?.premium;

    const hasMarketProducts = brands.length > 0
      && ((marketProducts?.length > 0) || marketProductsHidden === true);
    const hasFashionFinderProducts = brands.length > 0
      && fashionFinderProducts?.length > 0;

    const showProducts = premium && hasMarketProducts;
    const showFashionFinder = !showProducts && hasFashionFinderProducts;

    if (mode === 'products' && !showProducts) {
      redirectToRoute('company-profile', { slug }, res, 307);
    }

    if (mode === 'fashion-finder' && !showFashionFinder) {
      redirectToRoute('company-profile', { slug }, res, 307);
    }

    return {};
  };
};

export default getRedirectCompanyProducts;
